import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSettingsStore, type BibleTheme } from '@/lib/settings-store';
import styles from './BibleThemePicker.module.css';

/**
 * Reader header theme picker (Bible reader rethink, Chunk A). A single 44x44
 * icon button that opens a small popover of three options (Paper / Night /
 * Light). Selecting one calls setBibleTheme, which BibleChapter applies to the
 * reader .container via data-bible-theme.
 *
 * NOT a NavStack layer: it is a small anchored menu, not a full-screen overlay,
 * so it follows the lightweight-popover precedent (document-level click-outside
 * + Escape, attached only while open) used by NameHintInline / PrivateBadge.
 * Chrome is px-fixed (rule #15); the 44px button clears the touch floor (rule
 * #16). The popover's own colors read the reader theme tokens so it matches the
 * surface it sits on.
 */
export default function BibleThemePicker() {
  const { t } = useTranslation();
  const bibleTheme = useSettingsStore((s) => s.bibleTheme);
  const setBibleTheme = useSettingsStore((s) => s.setBibleTheme);
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  // Document-level click-outside + Escape, attached only while open.
  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: PointerEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const options: Array<{ value: BibleTheme; labelKey: string }> = [
    { value: 'paper', labelKey: 'bible.themePaper' },
    { value: 'night', labelKey: 'bible.themeNight' },
    { value: 'light', labelKey: 'bible.themeLight' },
  ];

  const handleSelect = (value: BibleTheme) => {
    setBibleTheme(value);
    setOpen(false);
  };

  return (
    <div className={styles.wrapper} ref={wrapRef}>
      <button
        type="button"
        className={styles.trigger}
        aria-haspopup="true"
        aria-expanded={open}
        aria-label={t('bible.themeLabel')}
        onClick={() => setOpen((v) => !v)}
      >
        {/* Appearance / contrast glyph: a circle, half-filled. Stroke +
            half-fill use currentColor so it follows the theme. */}
        <svg
          width="22"
          height="22"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <circle cx="12" cy="12" r="9" />
          <path d="M12 3a9 9 0 0 1 0 18z" fill="currentColor" stroke="none" />
        </svg>
      </button>
      {open && (
        <div
          className={styles.popover}
          role="radiogroup"
          aria-label={t('bible.themeLabel')}
        >
          {options.map((opt) => {
            const active = bibleTheme === opt.value;
            return (
              <button
                key={opt.value}
                type="button"
                role="radio"
                aria-checked={active}
                className={`${styles.option} ${active ? styles.optionActive : ''}`.trim()}
                onClick={() => handleSelect(opt.value)}
              >
                <span className={styles.optionLabel}>{t(opt.labelKey)}</span>
                {active && (
                  <svg
                    className={styles.check}
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
